import { Box, Button, Collapse, DialogContent, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlineOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CompanyContactList from '../../CompanyContactList';
import CopyToClipboard from '../../../common/components/CopyToClipboard';
import { ellipsisStyle } from '../../../common/utils/style.utils';
import { BuyerInfoViewProps } from './BuyerInfoDialog';

export default function BuyerInfoInvoicingContactsView({ company, close, changeView }: BuyerInfoViewProps): React.JSX.Element {
  const { t } = useTranslation(['common', 'contracts']);
  const [currentExpandedAccordion, setCurrentExpandedAccordion] = useState(false);

  const invoicingContacts = useMemo(() => {
    return company.role_assignments.filter(({ is_invoicing_contact }) => is_invoicing_contact).map(({ user }) => user);
  }, [company]);

  const toggleAccordion = (): void => {
    setCurrentExpandedAccordion(!currentExpandedAccordion);
  };

  return (
    <DialogContent
      sx={{
        maxWidth: 500,
        height: 620,
        p: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="text"
            onClick={() => changeView('INVOICING')}
          >
            <ArrowBackIcon />
          </Button>
          <PhoneIcon sx={{ mx: 1 }} />
          <Typography variant="titleMedium" sx={{ flex: 1 }}>
            {t('contracts:invoicing_contacts')}
          </Typography>
          <Button variant="text" onClick={close}>
            <CloseIcon />
          </Button>
        </Box>
        <Typography variant="bodySmall" color="secondary">
          {t('contracts:invoicing_contacts_description')}
        </Typography>
      </Box>

      <Box
        sx={{
          overflow: 'auto',
        }}
      >
        <List>
          <ListItem>
            <ListItemIcon>
              <PhoneIcon />
            </ListItemIcon>
            <ListItemText>
              {company.invoicing_phone_number || '-'}
            </ListItemText>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon>
              <EmailIcon />
            </ListItemIcon>
            <ListItemText>
              {company.invoicing_email ? (
                <CopyToClipboard textToCopy={company.invoicing_email}>
                  <Typography
                    variant="bodyMedium"
                    sx={{
                      ...ellipsisStyle,
                    }}
                  >
                    {company.invoicing_email}
                  </Typography>
                </CopyToClipboard>
              ) : '-'}
            </ListItemText>
          </ListItem>

          <Divider />

          <ListItemButton onClick={() => toggleAccordion()}>
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary={t('common:contacts')} />
            <ListItemIcon>
              {currentExpandedAccordion ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemIcon>
          </ListItemButton>
          <Collapse in={currentExpandedAccordion} timeout="auto" unmountOnExit sx={{ p: 2 }}>
            {invoicingContacts.length
              ? (
                <CompanyContactList contacts={invoicingContacts} displayContactInfo />
              ) : (
                <Typography variant="bodySmall" color="secondary" sx={{ px: 2, pt: 1, pb: 2 }}>
                  {t('contracts:no_invoicing_contact_registered')}
                </Typography>
              )}
          </Collapse>
        </List>
      </Box>
    </DialogContent>
  );
}
